import React, { useEffect, useState } from 'react';
import { FiArrowLeft, FiChevronLeft, FiX } from 'react-icons/fi';
import { Button, IconButton } from '../../../design-system';
import { openExternalUrl } from '../../../services/webviewService';
import { ProductSearchResults } from './ProductSearchResults';
import { ProductCopy, ProductMedia, ProductSummary } from './product';

interface SearchProduct extends ProductSummary {
  link: string;
  is_same_city?: boolean;
  is_vendor_online?: boolean;
  is_best_vendor?: boolean;
}

interface ProductSearchResultSetProps {
  products: SearchProduct[];
  onProductClick: (productId: number) => void;
  agent_chat_id?: number;
}

export const ProductSearchResultSet: React.FC<ProductSearchResultSetProps> = ({ products, onProductClick, agent_chat_id }) => {
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!showAll) return;
    const close = (event: KeyboardEvent) => event.key === 'Escape' && setShowAll(false);
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [showAll]);

  const openProduct = (product: SearchProduct) => {
    onProductClick(product.product_id);
    openExternalUrl(product.link);
  };

  return (
    <section className="overflow-hidden rounded-ds-12 border border-ds-100 bg-ds-white">
      <header className="flex h-12 items-center justify-between gap-3 border-b border-ds-100 px-3.5">
        <b className="text-[13px] text-ds-ink">نتیجه‌های جست‌وجوی محصول</b>
        <span className="text-[10px] text-ds-500">{products.length.toLocaleString('fa-IR')} محصول</span>
      </header>

      <div className="ds-product-strip p-3.5">
        {products.slice(0, 5).map((product) => (
          <button
            key={product.product_id}
            type="button"
            onClick={() => openProduct(product)}
            className="group w-[164px] shrink-0 snap-start overflow-hidden rounded-ds-16 border border-ds-100 bg-ds-white text-right transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-ds-200 hover:shadow-ds-2 app-sm:w-[184px]"
          >
            <ProductMedia product={{ ...product, is_top_vendor: product.is_best_vendor }} />
            <div className="flex min-h-[180px] flex-col p-3 app-sm:min-h-[194px] app-sm:p-4"><ProductCopy product={product} /></div>
          </button>
        ))}
      </div>

      <button type="button" onClick={() => setShowAll(true)} className="flex h-11 w-full items-center justify-center gap-2 border-t border-ds-100 bg-ds-canvas text-xs font-bold text-ds-primary-strong hover:bg-ds-primary-soft">
        مشاهده همه محصولات <FiChevronLeft />
      </button>

      {showAll && (
        <div className="fixed inset-0 z-50 flex flex-col bg-ds-canvas">
          <header className="flex h-[58px] shrink-0 items-center gap-3 border-b border-ds-100 bg-ds-white px-3 app-sm:h-[72px] app-sm:px-6">
            <IconButton onClick={() => setShowAll(false)} aria-label="بازگشت"><FiArrowLeft className="rtl-flip" /></IconButton>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-sm font-bold text-ds-ink">همه نتایج جست‌وجو</h2>
              <p className="mt-1 text-[10px] text-ds-500">{products.length.toLocaleString('fa-IR')} محصول</p>
            </div>
            <Button variant="secondary" className="hidden app-sm:inline-flex" onClick={() => setShowAll(false)}><FiX />بستن</Button>
          </header>
          <main className="ds-scrollbar min-h-0 flex-1 overflow-y-auto p-3 app-sm:p-6">
            <div className="mx-auto max-w-[1120px]">
              <ProductSearchResults products={products} onProductClick={onProductClick} agent_chat_id={agent_chat_id} />
            </div>
          </main>
        </div>
      )}
    </section>
  );
};
